<template>
  <div class="ai-assistant">
    <!-- 悬浮球（支持拖动，松手吸附左右边缘） -->
    <div
      v-if="!open"
      class="ai-fab"
      :class="{ dragging: isDragging }"
      :style="fabStyle"
      title="AI 助手"
      @click="handleClick"
      @touchstart="onDragStart"
      @touchmove.prevent="onDragMove"
      @touchend="onDragEnd"
      @touchcancel="onDragEnd"
      @mousedown="onDragStart"
    >
      <el-icon :size="26"><MagicStick /></el-icon>
    </div>

    <!-- 对话窗口：PC 右下角弹窗，移动端全屏 -->
    <transition name="ai-fade">
      <div v-if="open" class="ai-window" :class="{ mobile: isMobile }">
        <div class="ai-window-header">
          <span>AI 助手</span>
          <div class="header-actions">
            <el-icon class="action" title="清空对话" @click="clearChat"><Delete /></el-icon>
            <el-icon class="action" title="关闭" @click="open = false"><Close /></el-icon>
          </div>
        </div>
        <div class="ai-window-body">
          <AiChatPanel ref="panelRef" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { MagicStick, Close, Delete } from '@element-plus/icons-vue'
import AiChatPanel from './AiChatPanel.vue'
import { checkAiAvailable } from '@/utils/aiGate'

const open = ref(false)
const isMobile = ref(false)
const panelRef = ref<InstanceType<typeof AiChatPanel>>()

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  // 视口变化时把悬浮球收回屏幕内
  fabPos.value = clampPos(fabPos.value.x, fabPos.value.y)
}

// 可视区域变化时保持悬浮球在可视范围内
const keepInView = () => {
  fabPos.value = clampPos(fabPos.value.x, fabPos.value.y)
}

const clearChat = () => {
  panelRef.value?.clear()
}

// ===== 悬浮球拖动 =====
const FAB_SIZE = 52
const EDGE_GAP = 8
const POS_KEY = 'aiFabPos'

const fabPos = ref({ x: 0, y: 0 })
const isDragging = ref(false)
const fabStyle = computed(() => ({
  left: fabPos.value.x + 'px',
  top: fabPos.value.y + 'px',
  right: 'auto',
  bottom: 'auto'
}))

let startX = 0
let startY = 0
let startLeft = 0
let startTop = 0
let dragging = false
let moved = false
let suppressClick = false

// 可视视口（visualViewport 在移动端浏览器 UI 伸缩/innerWidth 不准时仍准确）
const getViewport = () => {
  const vv = window.visualViewport
  return {
    left: vv?.offsetLeft || 0,
    top: vv?.offsetTop || 0,
    width: vv?.width || window.innerWidth,
    height: vv?.height || window.innerHeight
  }
}

const clampPos = (x: number, y: number) => {
  const vp = getViewport()
  return {
    x: Math.min(Math.max(vp.left, x), Math.max(vp.left, vp.left + vp.width - FAB_SIZE)),
    y: Math.min(Math.max(vp.top, y), Math.max(vp.top, vp.top + vp.height - FAB_SIZE))
  }
}

const defaultPos = () => {
  const vp = getViewport()
  return clampPos(vp.left + vp.width - 24 - FAB_SIZE, vp.top + vp.height - 80 - FAB_SIZE)
}

const getPoint = (e: TouchEvent | MouseEvent) => {
  if ('touches' in e && e.touches.length > 0) return { x: e.touches[0].clientX, y: e.touches[0].clientY }
  if ('changedTouches' in e && e.changedTouches.length > 0) return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY }
  return { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY }
}

const onDragStart = (e: TouchEvent | MouseEvent) => {
  const p = getPoint(e)
  startX = p.x
  startY = p.y
  startLeft = fabPos.value.x
  startTop = fabPos.value.y
  dragging = true
  moved = false
  if (e.type === 'mousedown') {
    window.addEventListener('mousemove', onDragMove)
    window.addEventListener('mouseup', onDragEnd)
  }
}

const onDragMove = (e: TouchEvent | MouseEvent) => {
  if (!dragging) return
  const p = getPoint(e)
  const dx = p.x - startX
  const dy = p.y - startY
  // 超过 6px 才算拖动，避免轻触抖动误触发
  if (!moved && Math.abs(dx) < 6 && Math.abs(dy) < 6) return
  moved = true
  isDragging.value = true
  fabPos.value = clampPos(startLeft + dx, startTop + dy)
}

const onDragEnd = () => {
  if (!dragging) return
  dragging = false
  isDragging.value = false
  window.removeEventListener('mousemove', onDragMove)
  window.removeEventListener('mouseup', onDragEnd)
  if (moved) {
    // 吸附到较近一侧边缘
    const vp = getViewport()
    const centerX = fabPos.value.x + FAB_SIZE / 2
    const x = centerX < vp.left + vp.width / 2 ? vp.left + EDGE_GAP : vp.left + vp.width - FAB_SIZE - EDGE_GAP
    fabPos.value = clampPos(x, fabPos.value.y)
    localStorage.setItem(POS_KEY, JSON.stringify(fabPos.value))
    // 抑制松手后触发的 click，并兜底超时恢复
    suppressClick = true
    setTimeout(() => { suppressClick = false }, 350)
  }
  moved = false
}

const handleClick = async () => {
  if (suppressClick) {
    suppressClick = false
    return
  }
  // 悬浮球常显（可发现性），点击时实时校验 AI 状态，不可用则告知原因
  if (await checkAiAvailable({ featureName: 'AI 助手' })) {
    open.value = true
  }
}

onMounted(async () => {
  checkMobile()
  // 恢复上次拖动位置
  try {
    const saved = localStorage.getItem(POS_KEY)
    fabPos.value = saved ? clampPos(JSON.parse(saved).x, JSON.parse(saved).y) : defaultPos()
  } catch {
    fabPos.value = defaultPos()
  }
  window.addEventListener('resize', checkMobile)
  // 浏览器地址栏伸缩/横竖屏切换时可视区域变化，修正悬浮球位置
  window.visualViewport?.addEventListener('resize', keepInView)
  window.visualViewport?.addEventListener('scroll', keepInView)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
  window.visualViewport?.removeEventListener('resize', keepInView)
  window.visualViewport?.removeEventListener('scroll', keepInView)
  window.removeEventListener('mousemove', onDragMove)
  window.removeEventListener('mouseup', onDragEnd)
})
</script>

<style scoped>
.ai-fab {
  position: fixed;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409eff, #7b61ff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.4);
  z-index: 2000;
  transition: transform 0.2s, left 0.2s ease-out, top 0.2s ease-out;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.ai-fab:hover {
  transform: scale(1.08);
}
.ai-fab.dragging {
  transition: none;
  transform: scale(1.12);
  opacity: 0.9;
}
.ai-window {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 400px;
  height: 600px;
  max-height: calc(100vh - 48px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2001;
}
.ai-window.mobile {
  right: 0;
  bottom: 0;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  max-height: none;
  border-radius: 0;
}
.ai-window-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #409eff, #7b61ff);
  color: #fff;
  font-weight: 600;
}
.header-actions {
  display: flex;
  gap: 14px;
}
.header-actions .action {
  cursor: pointer;
  opacity: 0.9;
}
.header-actions .action:hover {
  opacity: 1;
}
.ai-window-body {
  flex: 1;
  overflow: hidden;
}
.ai-fade-enter-active,
.ai-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.ai-fade-enter-from,
.ai-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
